"use client"

// Theme provider + toggle for light/dark. The choice is persisted to
// localStorage and falls back to the OS preference on first load.

import { Moon, Sun } from "lucide-react"
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react"

export type Theme = "light" | "dark"

const STORAGE_KEY = "app-theme"

interface ThemeContextValue {
  theme: Theme
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

/** Resolve the initial theme: stored choice first, else OS preference. */
function getInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === "light" || stored === "dark") {
    return stored
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

/**
 * Provides the current theme and toggling, and reflects it onto
 * `document.documentElement` (the `.dark` class drives the token overrides).
 *
 * @param children - the app subtree.
 */
export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const [theme, setTheme] = useState<Theme>(getInitialTheme)

  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle("dark", theme === "dark")
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  const toggleTheme = useCallback(() => {
    setTheme(current => (current === "dark" ? "light" : "dark"))
  }, [])

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
}

/** Access the theme context. Throws if used outside ThemeProvider. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error("useTheme must be used within <ThemeProvider>.")
  }
  return ctx
}

/**
 * Read whether dark mode is active by observing the `.dark` class on the
 * document root. Provider-independent (safe in tests and outside ThemeProvider)
 * and re-renders when the theme is toggled.
 */
export function useIsDark(): boolean {
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== "undefined" ? document.documentElement.classList.contains("dark") : false
  )
  useEffect(() => {
    const root = document.documentElement
    const update = () => setIsDark(root.classList.contains("dark"))
    update()
    // The class is toggled by ThemeProvider; observe it so consumers restyle
    // live without threading the theme through props.
    const observer = new MutationObserver(update)
    observer.observe(root, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [])
  return isDark
}

/**
 * Icon button that toggles light/dark, with a Spanish accessible label.
 *
 * @param className - extra classes merged onto the button.
 */
export function ThemeToggle({ className }: { className?: string }): ReactNode {
  const { theme, toggleTheme } = useTheme()
  const label = theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      title={label}
      className={
        "inline-flex size-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring " +
        (className ?? "")
      }
    >
      {theme === "dark" ? (
        <Sun className="size-4" aria-hidden="true" />
      ) : (
        <Moon className="size-4" aria-hidden="true" />
      )}
    </button>
  )
}
